import isPromise from 'is-promise';
import React from 'react';
import useContext from '../../../../../hooks/useContext';
import { IApiaField, IApiaForm } from '../../../../../JSApi/types';
import {
  TFieldEvent,
  TFieldScriptEvent,
  TFormScriptEvent,
} from '../../../../../types';
import { notify } from '../../../../common/notifications/util';
import { ApiaComponentContextPrimitive } from '../../apiaComponent/ApiaComponentContext';
import { ApiaFormContext } from '../../page/ApiaForm';
import {
  getFunctionParams,
  showScriptFnErrorMessage,
} from '../../utils/scriptEvents';

export const callScriptFunction = async <
  SourceEvent extends IApiaField | IApiaForm = IApiaField,
>(
  eventSource: SourceEvent,
  formSource: IApiaForm,
  functionDefinition: TFormScriptEvent | TFieldScriptEvent,
): Promise<boolean> => {
  try {
    const fn = (window as unknown as Record<string, unknown>)[
      functionDefinition.fncName
    ] as (
      src: IApiaField | IApiaForm,
      ...parameters: unknown[]
    ) => Promise<boolean>;
    if (fn) {
      const fncParams = getFunctionParams(
        functionDefinition.fncParams,
        formSource,
      );
      const hasExecutedScript = fn(eventSource, ...fncParams);
      if (isPromise(hasExecutedScript)) {
        const hasActuallyExecutedScript = await hasExecutedScript;
        return hasActuallyExecutedScript;
      }
      return hasExecutedScript;
    }
    if (!fn) {
      showScriptFnErrorMessage(functionDefinition.fncName);
    }
  } catch (e) {
    console.error(e);
    notify({ message: String(e), type: 'danger' });
  }
  return false;
};

export default function useScriptEvents() {
  const { field, fieldId } = useContext(ApiaComponentContextPrimitive);
  const {
    formData: { formName },
    frmParent,
  } = useContext(ApiaFormContext);
  const { scriptEvents } = field;

  const fireScriptEvents = React.useCallback(
    async (type: TFieldEvent) => {
      try {
        const events = (scriptEvents ?? []).filter((script) => {
          return script.evtName === type;
        });
        if (events.length > 0) {
          const frm = window.ApiaFunctions.getForm(formName, frmParent);
          if (!frm) return false;

          let hasFailed = false;
          for (let i = 0; i < events.length; i++) {
            const sourceEvt = frm?.getFieldById(fieldId ?? '');
            const functionInstance = (
              window as unknown as Record<string, unknown>
            )[events[i].fncName];
            if (!functionInstance) {
              notify({
                message: `Can't find business class ${events[i].fncName}`,
              });
              return false;
            }
            let hasSucceed = false;
            if (sourceEvt) {
              hasSucceed = await callScriptFunction(sourceEvt, frm, events[i]);
            }
            if (!hasSucceed) {
              hasFailed = true;
              break;
            }
          }
          return !hasFailed;
        }
        return true;
      } catch (e) {
        notify({ message: String(e), type: 'danger' });
      }
      return false;
    },
    [fieldId, formName, frmParent, scriptEvents],
  );

  return fireScriptEvents;
}
